import type { LrapEnrollmentStatus } from 'tio-data/requests/enrollment';

/**
 * The ELF tile's visual state, derived from the server enrollment status.
 * `in-progress` maps the future enrollment state machine's mid-flow value
 * (`pending_signature` — the employee initiated signing but hasn't finished);
 * until the backend writes it, eligible-but-unenrolled users always land on
 * `blank`. `pending_enrollment` is deliberately NOT in-progress: per RFC 011
 * that state is set when the eligibility file assigns the LRAP plan — before
 * the employee has done anything — so it reads "Need to enroll" (blank).
 */
export type ElfTileState = 'blank' | 'in-progress';

export function elfTileState(status: LrapEnrollmentStatus | null): ElfTileState {
  return status?.enrollmentStatus === 'pending_signature' ? 'in-progress' : 'blank';
}

export function isLrapEnrolled(status: LrapEnrollmentStatus | null) {
  return status?.status === 'enrolled';
}

/**
 * Whether the SLR dashboard shows the ELF enrollment tile. The feature flag
 * remains the outer gate; within it:
 * - null status (endpoint outage or not yet deployed) fails open to showing
 *   the tile — the wizard itself still guards enrollment;
 * - ineligible users (no LRAP plan) never see the tile, aligning with RFC
 *   011's plan-level gating;
 * - enrolled users see the regular SLR dashboard instead of the tile.
 */
export function shouldShowElfTile(flagEnabled: boolean, status: LrapEnrollmentStatus | null) {
  if (!flagEnabled) return false;
  if (!status) return true;
  return status.lrapEligible && status.status !== 'enrolled';
}
